import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Mountain, Bot, FolderKanban, BellRing, Scale, ListChecks, Check, ArrowRight,
    ShieldCheck, FileSearch, Clock, ClipboardList, Layers,
} from "lucide-react";
import { DISCLAIMER } from "@/lib/constants";

const HERO = "https://images.unsplash.com/photo-1771668856954-80ec1ffdea7c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHw0fHxvcGVuJTIwcGl0JTIwbWluaW5nJTIwZXhjYXZhdG9yfGVufDB8fHx8MTc4ODM5NDI3Nnww&ixlib=rb-4.1.0&q=85";

const FEATURES = [
    { icon: Bot, title: "ASSISTENTE ANM IA", desc: "Respostas sobre a legislação mineral vigente no país" },
    { icon: FolderKanban, title: "MEUS PROCESSOS", desc: "Centralize sua carteira de processos minerários em um só lugar." },
    { icon: BellRing, title: "ALERTAS", desc: "Não perca prazos importantes com cálculo automático de dias restantes." },
    { icon: Scale, title: "LEGISLAÇÃO", desc: "Encontre rapidamente normas relacionadas à mineração com fonte oficial." },
    { icon: ListChecks, title: "CHECKLISTS", desc: "Organize procedimentos e documentos por regime e fase." },
];

const STEPS = [
    { n: 1, t: "Cadastre seus processos", icon: FolderKanban },
    { n: 2, t: "Organize prazos e obrigações", icon: Clock },
    { n: 3, t: "Receba alertas", icon: BellRing },
    { n: 4, t: "Consulte a IA e a legislação", icon: FileSearch },
    { n: 5, t: "Acompanhe sua carteira", icon: Layers },
];

const PLANS = [
    {
        key: "essencial", name: "GM² Essencial", price: "R$ 749", tagline: "Para o profissional autônomo",
        popular: false,
        features: ["3 processos ANM", "200 consultas IA/mês", "500 mil tokens/mês", "1 usuário"],
    },
    {
        key: "avancado", name: "GM² Avançado", price: "R$ 1.390", tagline: "Para escritórios em crescimento",
        popular: true,
        features: ["5 processos ANM", "400 consultas IA/mês", "1.000 mil tokens/mês", "2 usuários"],
    },
    {
        key: "consultoria", name: "GM² Consultoria", price: "R$ 3.190", tagline: "Para consultorias e equipes",
        popular: false,
        features: ["10 processos ANM", "800 consultas IA/mês", "2.000 mil tokens/mês", "3 usuários"],
    },
];

export default function Landing() {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-white">
            <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-200/60">
                <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-2 font-heading font-black text-lg text-petroleum" data-testid="landing-logo">
                        <span className="inline-flex items-center justify-center px-2 h-7 rounded bg-mineral text-white text-xs font-black">GM² ANM</span> GEOMINE MANAGER
                    </div>
                    <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
                        <a href="#recursos" className="hover:text-petroleum transition-colors">Recursos</a>
                        <a href="#como" className="hover:text-petroleum transition-colors">Como funciona</a>
                        <a href="#preco" className="hover:text-petroleum transition-colors">Preço</a>
                    </nav>
                    <div className="flex items-center gap-3">
                        <button onClick={() => navigate("/login")} className="text-sm font-semibold text-petroleum hover:text-petroleumHover transition-colors" data-testid="nav-login-button">Entrar</button>
                        <button onClick={() => navigate("/login")} className="bg-mineral hover:bg-mineralHover text-white rounded-sm px-4 py-2 text-sm font-bold shadow-md transition-colors" data-testid="nav-cta-button">Começar agora</button>
                    </div>
                </div>
            </header>

            {/* HERO */}
            <section className="relative bg-darkblue text-white overflow-hidden">
                <img src={HERO} alt="Mineração" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-r from-darkblue via-darkblue/90 to-petroleum/40" />
                <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-36">
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
                        <span className="inline-block text-mineral font-bold tracking-[0.2em] text-xs uppercase mb-4">Inteligência e Gestão de Processos Minerários</span>
                        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">GEOMINE MANAGER <span className="text-mineral">GM² ANM</span></h1>
                        <p className="mt-6 text-lg text-white/80 leading-relaxed">
                            Controle seus processos minerários, acompanhe prazos, consulte legislação e tenha uma IA especializada para auxiliar sua rotina profissional.
                        </p>
                        <div className="mt-10 flex flex-col sm:flex-row gap-4">
                            <button onClick={() => navigate("/login")} className="bg-mineral hover:bg-mineralHover text-white rounded-sm px-8 py-3.5 font-bold shadow-lg flex items-center justify-center gap-2 transition-colors" data-testid="hero-start-button">
                                Começar agora <ArrowRight className="w-5 h-5" />
                            </button>
                            <a href="#recursos" className="border-2 border-white/30 hover:border-white text-white rounded-sm px-8 py-3.5 font-semibold flex items-center justify-center transition-colors" data-testid="hero-know-button">
                                Conhecer a plataforma
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* FEATURES */}
            <section id="recursos" className="max-w-7xl mx-auto px-6 py-20 md:py-28">
                <div className="max-w-2xl">
                    <h2 className="font-heading text-3xl md:text-4xl font-black text-slate-900 tracking-tight">Tudo que você precisa para gerenciar sua carteira mineral</h2>
                    <p className="mt-4 text-slate-600">Uma plataforma B2B pensada para consultorias, empresas e profissionais da mineração brasileira.</p>
                </div>
                <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {FEATURES.map((f, i) => (
                        <motion.div key={f.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                            className="group bg-white border border-slate-200 rounded-xl p-7 hover:shadow-lg hover:border-petroleum/40 transition-all">
                            <div className="w-12 h-12 rounded-md bg-petroleum/10 flex items-center justify-center group-hover:bg-petroleum transition-colors">
                                <f.icon className="w-6 h-6 text-petroleum group-hover:text-white transition-colors" />
                            </div>
                            <h3 className="mt-5 font-heading font-bold text-slate-900 tracking-tight">{f.title}</h3>
                            <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* STEPS */}
            <section id="como" className="bg-slate-50 border-y border-slate-200 topo-texture">
                <div className="max-w-7xl mx-auto px-6 py-20 md:py-28">
                    <h2 className="font-heading text-3xl md:text-4xl font-black text-slate-900 text-center tracking-tight">Como funciona</h2>
                    <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
                        {STEPS.map((s) => (
                            <div key={s.n} className="bg-white border border-slate-200 rounded-xl p-6 text-center">
                                <div className="mx-auto w-12 h-12 rounded-full bg-mineral text-white flex items-center justify-center font-heading font-black text-lg">{s.n}</div>
                                <s.icon className="w-6 h-6 text-petroleum mx-auto mt-4" />
                                <p className="mt-3 text-sm font-semibold text-slate-700">{s.t}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PRICING */}
            <section id="preco" className="max-w-7xl mx-auto px-6 py-20 md:py-28">
                <div className="text-center max-w-2xl mx-auto">
                    <span className="text-mineral font-bold tracking-[0.15em] text-xs uppercase">Planos</span>
                    <h3 className="font-heading text-3xl md:text-4xl font-black mt-2 text-slate-900 tracking-tight">Escolha o plano ideal para você</h3>
                    <p className="mt-3 text-slate-600">Cobrança recorrente mensal · Cancele quando quiser.</p>
                </div>
                <div className="mt-12 grid md:grid-cols-3 gap-5 items-start max-w-5xl mx-auto">
                    {PLANS.map((p) => (
                        <div key={p.key} data-testid={`landing-plan-${p.key}`}
                            className={`rounded-2xl p-7 shadow-xl relative overflow-hidden ${p.popular ? "bg-darkblue text-white ring-2 ring-mineral md:-mt-3 md:mb-3" : "bg-white text-slate-900 border border-slate-200"}`}>
                            {p.popular && <div className="absolute inset-0 opacity-10 topo-texture" />}
                            {p.popular && <span className="absolute top-4 right-4 text-[11px] font-bold bg-mineral text-white px-2 py-0.5 rounded-full z-10">MAIS POPULAR</span>}
                            <div className="relative">
                                <h4 className="font-heading text-xl font-black">{p.name}</h4>
                                <p className={`text-xs mt-1 ${p.popular ? "text-white/60" : "text-slate-500"}`}>{p.tagline}</p>
                                <div className="mt-4 flex items-end gap-1">
                                    <span className="font-heading text-4xl font-black">{p.price}</span>
                                    <span className={`mb-1 text-sm ${p.popular ? "text-white/60" : "text-slate-400"}`}>/mês</span>
                                </div>
                                <ul className={`mt-5 space-y-2 text-sm ${p.popular ? "text-white/90" : "text-slate-700"}`}>
                                    {p.features.map((f) => (
                                        <li key={f} className="flex gap-2"><Check className="w-4 h-4 text-mineral shrink-0 mt-0.5" /> {f}</li>
                                    ))}
                                </ul>
                                <button onClick={() => navigate(`/assinatura?plan=${p.key}`)} data-testid={`landing-subscribe-${p.key}`}
                                    className={`mt-7 w-full rounded-sm py-3.5 font-bold shadow-lg transition-colors ${p.popular ? "bg-mineral hover:bg-mineralHover text-white" : "bg-petroleum hover:bg-petroleumHover text-white"}`}>
                                    Assinar
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
                <p className="text-center text-xs text-slate-400 mt-6">Precisa de mais processos? Adicione Processos Adicionais depois de assinar.</p>
            </section>

            <footer className="bg-darkblue text-white">
                <div className="max-w-7xl mx-auto px-6 py-12">
                    <div className="flex flex-col md:flex-row justify-between gap-8">
                        <div className="max-w-sm">
                            <div className="flex items-center gap-2 font-heading font-black text-lg"><Mountain className="w-6 h-6 text-mineral" /> GEOMINE MANAGER</div>
                            <p className="mt-4 text-sm text-white/60 leading-relaxed">{DISCLAIMER}</p>
                        </div>
                        <div className="flex gap-12 text-sm">
                            <div className="space-y-2">
                                <div className="font-semibold text-white/90 mb-2">Legal</div>
                                <Link to="/privacidade" className="block text-white/60 hover:text-mineral transition-colors">Política de Privacidade</Link>
                                <Link to="/termos" className="block text-white/60 hover:text-mineral transition-colors">Termos de Uso</Link>
                                <Link to="/cookies" className="block text-white/60 hover:text-mineral transition-colors">Política de Cookies</Link>
                            </div>
                            <div className="space-y-2">
                                <div className="font-semibold text-white/90 mb-2">Plataforma</div>
                                <button onClick={() => navigate("/login")} className="block text-white/60 hover:text-mineral transition-colors">Entrar</button>
                                <a href="#preco" className="block text-white/60 hover:text-mineral transition-colors">Assinatura</a>
                            </div>
                        </div>
                    </div>
                    <div className="mt-10 pt-6 border-t border-white/10 flex items-center gap-2 text-xs text-white/40">
                        <ShieldCheck className="w-4 h-4" /> Informações regulatórias devem ser confirmadas nas fontes oficiais (ANM / gov.br).
                    </div>
                </div>
            </footer>
        </div>
    );
}
